import { ref } from '@vue/composition-api'
import { CLIENT_ZIP_THRESHOLD_BYTES, formatBytes } from '@/common-utils'
import {
  supportsClientZip,
  downloadClientZip,
  ClientZipCancelled,
  isOrderFormFailure,
} from '@/utils/clientZip'
import { apiToastSuccess, apiToastFailure, apiToastWarning } from '@/@core/utils/toast'

// Chrome throws SecurityError when a frame may not open a save picker.
const isPickerRefused = err => Boolean(err && err.name === 'SecurityError')

const OVERSIZED_ON_SERVER = 'This browser can\'t build large downloads locally, so it is being prepared on the server. It may take several minutes — you can close this and collect it from Downloads.'

// Values may be plain or reactive getters, since the caller passes props.
const read = value => (typeof value === 'function' ? value() : value)

/**
 * The >2GB tier: deciding whether the browser should build the archive, and
 * running it if so.
 *
 * Split out of CollectionDownloadPicker, which was carrying three unrelated
 * concerns. Selection state and the per-row download request stay in the
 * component — those are about the picker. This is only about the zip, which
 * is why it can be exercised without mounting anything.
 *
 * `onStarted` fires once the download is genuinely underway (the save
 * location is chosen, or the stream is handed to the service worker); the
 * caller uses it to get out of the way.
 */
export function useClientZipDownload({
  collectionId,
  retailerId = null,
  zipName = null,
  onStarted = null,
}) {
  const clientZip = ref({ running: false, loadedBytes: 0 })

  const idle = () => {
    clientZip.value = { running: false, loadedBytes: 0 }
  }

  // Over the threshold AND the browser can actually do it.
  const shouldClientZip = bytes => supportsClientZip() && bytes > CLIENT_ZIP_THRESHOLD_BYTES

  // Over the threshold but the browser cannot build the zip itself, so the
  // caller falls back to the server bundler — the case this tier exists to
  // avoid. It still works, it is just slow, so say so rather than let the
  // user wonder why a large download is sitting in the tray.
  const warnIfDegraded = bytes => {
    if (bytes > CLIENT_ZIP_THRESHOLD_BYTES) apiToastWarning(OVERSIZED_ON_SERVER)
  }

  // The browser refuses the save picker in some frames (the embedded shell)
  // — deliver through the tray zip instead. Cancels stay silent.
  const handleFailure = (err, fallback) => {
    if (fallback && isPickerRefused(err)) {
      // Clear the state first: callers gate their own actions on `running`.
      idle()
      fallback()
      return
    }
    if (err instanceof ClientZipCancelled) return
    // Say which half failed. A missing order form used to be silent, which
    // is the whole reason this path generates one up front.
    if (isOrderFormFailure(err)) {
      apiToastFailure(err, `${err.message}. Nothing was downloaded — please try again.`)
      return
    }
    apiToastFailure(err, 'Could not build the ZIP. Please try again.')
  }

  const run = async ({ deliverable, selectedAssets, fallback }) => {
    clientZip.value = { running: true, loadedBytes: 0 }
    try {
      const result = await downloadClientZip({
        collectionId: read(collectionId),
        retailerId: read(retailerId),
        deliverable,
        selectedAssets,
        zipName: read(zipName) || 'collection-download',
        onStarted: () => onStarted && onStarted(),
        onProgress: ({ loaded }) => {
          clientZip.value = { ...clientZip.value, loadedBytes: loaded }
        },
      })
      apiToastSuccess(`Saved ${result.files} files (${formatBytes(result.bytes)}) to your device`)
    } catch (err) {
      handleFailure(err, fallback)
    } finally {
      idle()
    }
  }

  return {
    clientZip, shouldClientZip, warnIfDegraded, runClientZip: run,
  }
}
